import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Shield, TrendingUp, Handshake, Target } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const T = {
  heading: { de: 'Unsere Werte', en: 'Our Values' },
  sub: { de: 'Die Grundpfeiler unserer Unternehmensphilosophie', en: 'The cornerstones of our corporate philosophy' },
  desc: {
    de: 'Diese Werte verbinden alle Unternehmen der Basic United Holding und bilden das Fundament für nachhaltige Geschäftsbeziehungen.',
    en: 'These values connect all companies within Basic United Holding and form the foundation for sustainable business relationships.',
  },
};

const values = [
  { icon: Shield, title: { de: 'Verlässlichkeit', en: 'Reliability' }, description: { de: 'Wir halten, was wir versprechen. Unsere Partner und Kunden können sich auf uns verlassen – heute und in Zukunft.', en: 'We keep our promises. Our partners and clients can rely on us – today and in the future.' }, accent: false },
  { icon: TrendingUp, title: { de: 'Wachstum', en: 'Growth' }, description: { de: 'Kontinuierliche Weiterentwicklung und strategische Expansion sind die Grundpfeiler unseres Unternehmenshandelns.', en: 'Continuous development and strategic expansion are the cornerstones of our corporate actions.' }, accent: true },
  { icon: Handshake, title: { de: 'Integrität', en: 'Integrity' }, description: { de: 'Ehrlichkeit und Transparenz prägen unsere Geschäftsbeziehungen und bilden das Fundament unseres Erfolgs.', en: 'Honesty and transparency shape our business relationships and form the foundation of our success.' }, accent: false },
  { icon: Target, title: { de: 'Stärke', en: 'Strength' }, description: { de: 'Die gebündelte Kraft unserer Unternehmensgruppe schafft Synergien und nachhaltige Wettbewerbsvorteile.', en: 'The combined power of our corporate group creates synergies and sustainable competitive advantages.' }, accent: false }
];

export const ValuesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { language } = useLanguage();

  return (
    <section ref={ref} data-testid="values-section" className="section-spacing bg-[#0A1220]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="mb-16">
          <div className="accent-line mb-6"></div>
          <div className="grid lg:grid-cols-2 gap-8 items-end">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">{T.heading[language]}</h2>
              <p className="text-lg text-white/60">{T.sub[language]}</p>
            </div>
            <p className="text-white/50 leading-relaxed lg:text-right">{T.desc[language]}</p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
              data-testid={`value-card-${value.title.de.toLowerCase()}`}
              className={`p-8 rounded-2xl border transition-all duration-300 ${value.accent ? 'bg-[#F5A623] text-[#0F1A2E] border-[#F5A623]' : 'bg-[#152238] border-[#1e3a5f] hover:border-[#F5A623]/30'}`}
            >
              <div className={`w-14 h-14 flex items-center justify-center mb-6 rounded-xl ${value.accent ? 'bg-[#0F1A2E]' : 'bg-[#F5A623]/10'}`}>
                <value.icon size={28} className={value.accent ? 'text-[#F5A623]' : 'text-[#F5A623]'} />
              </div>
              <h3 className={`font-heading text-xl font-semibold mb-3 ${value.accent ? 'text-[#0F1A2E]' : 'text-white'}`}>{value.title[language]}</h3>
              <p className={`leading-relaxed ${value.accent ? 'text-[#0F1A2E]/80' : 'text-white/50'}`}>{value.description[language]}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;
